import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { updateSaleDepositExpenses } from "../../services";
import { SaleDepositResponse } from "../../types/sale-deposit.type";
import { formatNumber } from "../../utils";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";
import ExpenseEditFields from "./ExpenseEditFields";
import { buildExpenseLines } from "./transfer.utils";
import { useExpenseDraft } from "./useExpenseDraft";

const compact = `@media ${COMPACT_QUERY}`;

interface ExpenseDetailModalProps {
  open: boolean;
  /** The row whose expenses are shown — everything is already on it, no fetch needed. */
  deposit: SaleDepositResponse | null;
  /**
   * Whether this viewer's role may re-file the branch's expense figures. The server's other
   * refusal — a deposit inside a closed branch-day — is not visible from a list row, so it is
   * left to the server; a ตรวจแล้ว tick locks nothing.
   */
  canEdit: boolean;
  /** Fired after a successful save so the caller can refetch the row's recomputed totals. */
  onSaved: () => void | Promise<void>;
  onClose: () => void;
}

/**
 * รายละเอียดค่าใช้จ่าย for one deposit, and — for the branch back office — the one place the
 * figures can be corrected after the fact. All figures ride inside the list row already
 * (five fixed buckets + the free-form `additional_expenses` array + the pre-computed total),
 * so this opens instantly with no request.
 *
 * The expenses are filed once, on แจ้งยอดโอน, and freeze the moment a slip is uploaded. When
 * one of them is wrong — a 30 baht transfer fee where the salesperson only drew 15 from
 * company cash — the row carries a permanent ผลต่าง that nobody can clear. Editing here
 * re-files the figure and the server recomputes ที่ต้องโอน from it.
 *
 * In read mode the footer prints the SERVER's `total_expense_amount` rather than re-summing
 * the lines: that stored value is what `required_transfer_amount` was actually computed from,
 * so showing a locally recomputed total could disagree with the ยอดต้องโอน on the same
 * screen. While editing, the footer previews the draft's own sum — but the save reloads the
 * row, so what is displayed afterwards is the server's figure again, never the preview.
 */
export default function ExpenseDetailModal({
  open,
  deposit,
  canEdit,
  onSaved,
  onClose,
}: ExpenseDetailModalProps) {
  const [editing, setEditing] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const draft = useExpenseDraft();

  const lines = deposit ? buildExpenseLines(deposit) : [];

  // Closing the dialog, or switching to another row, must not leave a half-typed correction
  // to reappear over figures it was never typed against.
  useEffect(() => {
    if (!open) setEditing(false);
  }, [open]);

  useEffect(() => {
    setEditing(false);
  }, [deposit?.uuid]);

  const startEdit = () => {
    if (!deposit) return;
    draft.reset(deposit);
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const handleSave = async () => {
    if (!deposit) return;
    const request = draft.buildRequest();
    if (!request) return;

    setSaving(true);
    try {
      await updateSaleDepositExpenses(deposit.uuid, request);
      setEditing(false);
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const total = editing ? draft.previewTotal : deposit?.total_expense_amount;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            p: 0,
            maxHeight: "90vh",
            [compact]: { maxHeight: "94vh", m: 2 },
          },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        {editing ? "แก้ไขค่าใช้จ่าย" : "รายละเอียดค่าใช้จ่าย"}
        {deposit?.route_code ? `: ${deposit.route_code}` : ""}
        <IconButton
          aria-label="ปิด"
          onClick={handleClose}
          disabled={saving}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: "auto" }}>
        {editing ? (
          <ExpenseEditFields
            money={draft.money}
            additionalExpenses={draft.additionalExpenses}
            errors={draft.errors}
            disabled={saving}
            onChangeMoney={draft.updateMoney}
            onAddExpense={draft.addExpense}
            onChangeExpense={draft.updateExpense}
            onRemoveExpense={draft.removeExpense}
          />
        ) : lines.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
            ไม่มีรายการค่าใช้จ่าย
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {lines.map((line, index) => (
              <Stack
                key={`${line.label}-${index}`}
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
                gap={2}
                sx={{ py: 1.25 }}
              >
                <Typography variant="body2" sx={{ minWidth: 0 }}>
                  {line.label}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {formatNumber(line.amount)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, justifyContent: "space-between", gap: 2 }}
      >
        <Stack direction="row" alignItems="baseline" gap={1} sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {editing ? "รวมค่าใช้จ่าย (ก่อนบันทึก)" : "รวมค่าใช้จ่าย"}
          </Typography>
          <Typography fontWeight={700} sx={{ whiteSpace: "nowrap" }}>
            {formatNumber(total)} บาท
          </Typography>
        </Stack>
        {editing ? (
          <Stack direction="row" gap={1}>
            <Button color="inherit" onClick={cancelEdit} disabled={saving}>
              ยกเลิก
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              บันทึก
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" gap={1}>
            {canEdit && (
              <Button startIcon={<EditOutlinedIcon />} onClick={startEdit}>
                แก้ไข
              </Button>
            )}
            <Button variant="contained" onClick={onClose}>
              ปิด
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
}
